import type {
  CoworkFile,
  CoworkReportingWorkflowConfig,
  CoworkWorkflow,
  CoworkWorkflowRun,
  CoworkWorkflowRunSourceOutcome,
  CoworkWorkflowRunValidationFinding,
  CoworkWorkspaceAgent,
} from "@/cowork/api"
import {
  coworkAccountingReportTemplates,
  validAccountCardSelection,
} from "@/cowork/report-templates"

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

export function workflowReportAgents(
  agents: CoworkWorkspaceAgent[], scope: { companyID: string; projectID: string }, requiredSkills: readonly string[],
) {
  return agents.filter((agent) => {
    const skills = agent.metadata.allowed_skills
    return agent.status === "active" && agent.tools.generateReports === true &&
      Array.isArray(skills) && requiredSkills.every((skill) => skills.includes(skill)) &&
      agent.assignments.some((assignment) => assignment.scope_type === "workspace" ||
        (assignment.scope_type === "entity" && assignment.scope_id === scope.companyID) ||
        (assignment.scope_type === "project" && assignment.scope_id === scope.projectID))
  })
}

function snapshotText(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined
}

function parameterText(value: unknown) {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  if (value === null) return "null"
  if (value === undefined) return
  return JSON.stringify(value)
}

export type WorkflowExecutionDetails = {
  templateID?: string
  templateVersion?: number
  reportType?: string
  jurisdiction?: string | null
  runtimeAgent?: string
  catalogRuntimeAgent?: string
  requiredSkills: string[]
  parameters: { name: string; value: string }[]
}

export function workflowExecutionDetails(snapshot: Record<string, unknown>): WorkflowExecutionDetails | undefined {
  const execution = record(snapshot.execution)
  const capabilities = record(execution?.template_capabilities)
  if (!capabilities) return

  const reporting = record(snapshot.reporting)
  const parameters = record(reporting?.parameters)
  const accountCard = record(reporting?.account_card)
  const entries = [
    ...Object.entries(parameters ?? {}),
    ...Object.entries(accountCard ?? {}).map(([name, value]) => [`account_card.${name}`, value] as const),
  ].flatMap(([name, value]) => {
    const text = parameterText(value)
    return text === undefined ? [] : [{ name, value: text }]
  })
  const jurisdiction = capabilities.jurisdiction

  return {
    templateID: snapshotText(capabilities.template_id),
    templateVersion:
      typeof capabilities.template_version === "number" && Number.isInteger(capabilities.template_version)
        ? capabilities.template_version
        : undefined,
    reportType: snapshotText(capabilities.report_type),
    ...(jurisdiction === null || snapshotText(jurisdiction) ? { jurisdiction: jurisdiction as string | null } : {}),
    runtimeAgent: snapshotText(execution?.runtime_agent),
    catalogRuntimeAgent: snapshotText(capabilities.catalog_runtime_agent),
    requiredSkills: Array.isArray(capabilities.required_skills)
      ? [...new Set(capabilities.required_skills.filter((skill): skill is string => !!snapshotText(skill)))]
      : [],
    parameters: entries,
  }
}

export function readReportingConfig(value: unknown): CoworkReportingWorkflowConfig | undefined {
  const config = record(value)
  if (!config) return
  const fields = ["template_id", "report_type", "visible_prompt", "report_language"] as const
  if (fields.some((key) => typeof config[key] !== "string" || !(config[key] as string).trim())) return
  const template = coworkAccountingReportTemplates.find(
    (item) => item.id === config.template_id && item.reportType === config.report_type,
  )
  const customReport = config.template_id === "custom-report" && config.report_type === "custom_report"
  if (!template && !customReport) return
  if (customReport && config.template_version !== undefined && config.template_version !== 1) return
  if (!Array.isArray(config.file_ids) || !config.file_ids.length) return
  if (config.file_ids.some((id) => typeof id !== "string" || !id.trim())) return
  if (
    config.period_id !== undefined &&
    config.period_id !== null &&
    (typeof config.period_id !== "string" || !config.period_id.trim())
  )
    return
  if (config.template_id === "account-card" &&
    (!config.period_id || (config.account_card != null && !validAccountCardSelection(config.account_card)))) return
  if (config.template_id !== "account-card" && config.account_card != null) return
  return {
    ...(config.template_id === "account-card" && validAccountCardSelection(config.account_card)
      ? { account_card: { ...config.account_card } } : {}),
    template_id: config.template_id as string,
    template_version:
      typeof config.template_version === "number" && Number.isInteger(config.template_version)
        ? config.template_version
        : template?.version ?? 1,
    report_type: config.report_type as string,
    ...(typeof config.instructions === "string" && config.instructions ? { instructions: config.instructions } : {}),
    visible_prompt: config.visible_prompt as string,
    report_language: config.report_language as string,
    file_ids: [...new Set(config.file_ids as string[])],
    period_id: config.period_id as string | null | undefined,
  }
}

export function workflowWizardValues(workflow?: Pick<CoworkWorkflow, "name" | "workflow_metadata">) {
  const reporting = readReportingConfig(workflow?.workflow_metadata.reporting)
  return {
    name: workflow?.name ?? "",
    templateID: reporting?.template_id ?? "",
    fileIDs: reporting?.file_ids ?? [],
    reporting,
  }
}

export function workflowRunIndependentReview(run: CoworkWorkflowRun) {
  const review = record(run.output_snapshot.review)
  if (review?.contract_version !== "1" || !["ready", "qualified"].includes(String(review.verdict)) ||
    typeof review.summary !== "string" || !Array.isArray(review.findings)) return
  const findings = review.findings.filter((value): value is { location: string; message: string; suggested_fix: string } => {
    const finding = record(value)
    return !!finding && typeof finding.location === "string" && typeof finding.message === "string" &&
      typeof finding.suggested_fix === "string"
  })
  return { verdict: review.verdict as "ready" | "qualified", summary: review.summary, findings }
}

export function workflowRunReviewHistory(run: CoworkWorkflowRun) {
  const attempts = run.metadata.report_review_history
  if (!Array.isArray(attempts)) return []
  const current = record(run.output_snapshot.review)
  return attempts.flatMap((value, index) => {
    const review = record(value)
    if (!review || review.run_id !== run.id || review.contract_version !== "1" ||
      typeof review.summary !== "string" || !["ready", "qualified", "revise", "blocked"].includes(String(review.verdict))) return []
    if (current?.reviewer_session_id === review.reviewer_session_id &&
      (typeof current?.reviewer_message_id === "string" && typeof review.reviewer_message_id === "string"
        ? current.reviewer_message_id === review.reviewer_message_id
        : current?.candidate_revision_id === review.candidate_revision_id)) return []
    const findings = Array.isArray(review.findings)
      ? review.findings.flatMap((item) => {
        const finding = record(item)
        return finding && typeof finding.message === "string" && typeof finding.location === "string"
          ? [{ location: finding.location, message: finding.message }] : []
      }) : []
    return [{ index: index + 1, verdict: review.verdict as "ready" | "qualified" | "revise" | "blocked",
      summary: review.summary, findings }]
  })
}

export function workflowSourceFileAvailable(
  file: CoworkFile,
  scope: { companyID: string; projectID: string; periodID?: string | null },
) {
  const periodID = file.period_id ?? file.workflow_id
  return (
    !!file.id &&
    file.company_id === scope.companyID &&
    file.project_id === scope.projectID &&
    (!periodID || periodID === scope.periodID) &&
    ["uploaded", "processed", "ready"].includes(file.status)
  )
}

export function workflowSourceChoices(
  files: readonly CoworkFile[],
  selectedIDs: readonly string[],
  scope: { companyID: string; projectID: string; periodID?: string | null },
) {
  const available = files.filter((file) => workflowSourceFileAvailable(file, scope))
  return [
    ...available.map((file) => ({
      id: file.id,
      name: file.filename,
      available: true,
      selected: selectedIDs.includes(file.id),
    })),
    ...selectedIDs
      .filter((id) => !available.some((file) => file.id === id))
      .map((id) => ({ id, name: id, available: false, selected: true })),
  ]
}

export type WorkflowUpload = {
  key: string
  file: File
  status: "uploading" | "success" | "failure"
  fileID?: string
}

export function workflowUploadsReady(files: readonly Pick<WorkflowUpload, "status" | "fileID">[]) {
  return files.length > 0 && files.every((file) => file.status === "success" && !!file.fileID)
}

export function workflowRunIsActive(run: CoworkWorkflowRun) {
  return run.status === "created" || run.status === "running"
}

const stages = [
  "starting",
  "processing_files",
  "analyzing",
  "generating_report",
  "waiting_for_input",
  "ready",
  "needs_review",
  "failed",
  "unknown",
] as const

export function workflowRunStage(run: CoworkWorkflowRun): (typeof stages)[number] {
  if (run.status === "failed") return "failed"
  if (run.status === "needs_review") return "needs_review"
  if (run.status === "completed") {
    return run.validation_status === "passed" && run.progress_stage === "ready"
      ? "ready"
      : "needs_review"
  }
  if (!workflowRunIsActive(run)) return "unknown"
  if (run.progress_stage === "ready") return "unknown"
  return stages.includes(run.progress_stage) ? run.progress_stage : "unknown"
}

export function workflowSnapshotFiles(snapshot: Record<string, unknown>) {
  const reporting = record(snapshot.reporting)
  const ids = snapshot.file_ids ?? reporting?.file_ids
  const files = Array.isArray(snapshot.files) ? snapshot.files : []
  const names = new Map<string, string>()
  for (const file of files) {
    const item = record(file)
    if (item && typeof item.id === "string") {
      names.set(item.id, typeof item.filename === "string" ? item.filename : item.id)
    }
  }
  const selected = Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === "string" && !!id)
    : [...names.keys()]
  return [...new Set(selected)].map((id) => ({ id, name: names.get(id) ?? id }))
}

const findingClassifications = ["blocking", "incomplete", "warning"] as const
const sourceOutcomeStatuses = ["completed", "partial", "unreadable", "unsupported", "failed"] as const

export function workflowRunValidationFindings(run: CoworkWorkflowRun): CoworkWorkflowRunValidationFinding[] {
  const snapshot = record(run.output_snapshot)
  const value = Array.isArray(run.validation_findings)
    ? run.validation_findings
    : snapshot?.validation_findings
  if (!Array.isArray(value)) return []
  return value.filter((item): item is CoworkWorkflowRunValidationFinding => {
    const finding = record(item)
    return !!finding &&
      typeof finding.code === "string" &&
      typeof finding.message === "string" &&
      (finding.status === "passed" || finding.status === "failed") &&
      findingClassifications.includes(finding.classification as (typeof findingClassifications)[number])
  })
}

export function groupWorkflowRunValidationFindings(findings: readonly CoworkWorkflowRunValidationFinding[]) {
  return findingClassifications.flatMap((classification) => {
    const items = findings.filter((finding) => finding.classification === classification)
    return items.length ? [{ classification, findings: items }] : []
  })
}

function workflowRunSourceOutcomes(run: CoworkWorkflowRun): CoworkWorkflowRunSourceOutcome[] {
  const snapshot = record(run.output_snapshot)
  const value = Array.isArray(run.file_extraction_statuses)
    ? run.file_extraction_statuses
    : Array.isArray(run.source_outcomes)
      ? run.source_outcomes
      : snapshot?.source_outcomes
  if (!Array.isArray(value)) return []
  return value.filter((item): item is CoworkWorkflowRunSourceOutcome => {
    const outcome = record(item)
    return !!outcome &&
      typeof outcome.source_id === "string" &&
      sourceOutcomeStatuses.includes(outcome.status as (typeof sourceOutcomeStatuses)[number])
  })
}

export function workflowRunFileExtractionStatuses(run: CoworkWorkflowRun) {
  const outcomes = new Map(workflowRunSourceOutcomes(run).map((outcome) => [outcome.source_id, outcome]))
  return workflowSnapshotFiles(run.input_snapshot).map((file) => ({
    ...file,
    outcome: outcomes.get(file.id),
  }))
}

// Keep an uncertain network attempt's identity until it succeeds. Only explicit new intents get a new ID.
export function createWorkflowRunAttempt<T, R>(send: (input: T, requestID: string) => Promise<R>) {
  let pending: { input: T; requestID: string } | undefined
  let busy = false
  return {
    async start(input: T): Promise<R | undefined> {
      if (busy) return
      pending ??= { input, requestID: crypto.randomUUID() }
      busy = true
      try {
        const result = await send(pending.input, pending.requestID)
        pending = undefined
        return result
      } finally {
        busy = false
      }
    },
  }
}
